import { HTMLAttributes, useEffect } from 'react'
import { useMedia } from 'react-use'
import { default as NextImage } from 'next/image'

import LazyImage from '@/components/base/lazy-image'
import { MediaQuery } from '@/constants'

import { cn } from '@/lib/utils'
import { WPMedia } from '@/types'

export type MediaProps = HTMLAttributes<HTMLElement> &
  WPMedia & {
    size?: 'large' | 'medium' | 'thumbnail'
    lazy?: boolean
    onLoad?: () => void
  }

const Media = ({
  video,
  video_mobile,
  image,
  image_mobile,
  use_video,
  size,
  className,
  lazy = true,
  onLoad,
  ...props
}: MediaProps) => {
  const isSM = useMedia(MediaQuery.SMALL, true)
  const Image = lazy ? LazyImage : NextImage
  const imageSource = isSM && image_mobile?.url ? image_mobile : image
  const videoSource = isSM && video_mobile?.url ? video_mobile : video
  const imageUrl = size ? imageSource?.sizes?.[size]?.url || imageSource?.url : imageSource?.url
  const hasPoster = !!imageSource?.url

  useEffect(() => {
    if (use_video && !hasPoster) {
      onLoad?.()
    }
  }, [use_video, hasPoster, onLoad])

  return use_video ? (
    <>
      {hasPoster && (
        <img src={imageSource.url} alt="" onLoad={onLoad} className="sr-only" aria-hidden="true" />
      )}
      <video
        loop
        muted
        autoPlay
        playsInline
        poster={imageSource?.url}
        className={cn('absolute left-0 top-0 size-full', className)}
        {...props}
      >
        <source src={videoSource?.url} type="video/mp4" />
      </video>
    </>
  ) : imageUrl ? (
    <Image
      src={imageUrl}
      alt={imageSource?.alt || ''}
      className={cn(className)}
      fill
      onLoad={onLoad}
      {...props}
    />
  ) : null
}

export default Media
